import type {
  Chart,
  ChartData,
  ChartOptions,
  Plugin,
  ScatterDataPoint,
} from "chart.js";
import {
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  ScatterController,
  Tooltip,
} from "chart.js";
import { type ReactNode, type RefObject, useMemo } from "react";
import { Scatter } from "react-chartjs-2";

import { DEFAULT_SCATTER_CHART_OPTIONS } from "../lib/default-chart-options";
import { mergeChartConfig } from "../lib/merge-chart-config";

ChartJS.register(
  ScatterController,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

export type ChartJsScatterPoint = ScatterDataPoint & {
  label?: string;
  tooltipLabel?: string;
};

type ScatterQuadrantBackground = {
  xMidpoint: number;
  yMidpoint: number;
  topLeftColor: string;
  topRightColor: string;
  bottomLeftColor: string;
  bottomRightColor: string;
};

interface ChartJsScatterChartProps {
  chartRef?: RefObject<Chart<"scatter">>;
  data: ChartData<"scatter", ChartJsScatterPoint[]>;
  options?: ChartOptions<"scatter">;
  plugins?: Plugin<"scatter">[];
  quadrantBackground?: ScatterQuadrantBackground;
  showPointLabels?: boolean;
  children?: ReactNode;
}

function createQuadrantBackgroundPlugin(
  quadrantBackground: ScatterQuadrantBackground | undefined,
): Plugin<"scatter"> | null {
  if (!quadrantBackground) {
    return null;
  }

  return {
    id: "scatterQuadrantBackground",
    beforeDatasetsDraw: (chart) => {
      const { ctx, chartArea, scales } = chart;
      const xScale = scales.x;
      const yScale = scales.y;

      if (!chartArea || !xScale || !yScale) {
        return;
      }

      const xMidpoint = xScale.getPixelForValue(quadrantBackground.xMidpoint);
      const yMidpoint = yScale.getPixelForValue(quadrantBackground.yMidpoint);
      const { left, right, top, bottom } = chartArea;

      ctx.save();
      ctx.fillStyle = quadrantBackground.topLeftColor;
      ctx.fillRect(left, top, xMidpoint - left, yMidpoint - top);
      ctx.fillStyle = quadrantBackground.topRightColor;
      ctx.fillRect(xMidpoint, top, right - xMidpoint, yMidpoint - top);
      ctx.fillStyle = quadrantBackground.bottomLeftColor;
      ctx.fillRect(left, yMidpoint, xMidpoint - left, bottom - yMidpoint);
      ctx.fillStyle = quadrantBackground.bottomRightColor;
      ctx.fillRect(xMidpoint, yMidpoint, right - xMidpoint, bottom - yMidpoint);
      ctx.restore();
    },
  };
}

const scatterPointLabelPlugin: Plugin<"scatter"> = {
  id: "scatterPointLabels",
  afterDatasetsDraw: (chart) => {
    const { ctx } = chart;

    ctx.save();
    ctx.font = "600 11px 'Instrument Sans', sans-serif";
    ctx.fillStyle = "currentColor";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      meta.data.forEach((element, pointIndex) => {
        const point = dataset.data[pointIndex] as
          | ChartJsScatterPoint
          | undefined;

        if (!point?.label) {
          return;
        }

        ctx.fillText(point.label, element.x, element.y + 10);
      });
    });

    ctx.restore();
  },
};

function ChartJsScatterChart({
  chartRef,
  data,
  options,
  plugins,
  quadrantBackground,
  showPointLabels = false,
  children,
}: ChartJsScatterChartProps) {
  const mergedOptions = useMemo(
    () => mergeChartConfig(DEFAULT_SCATTER_CHART_OPTIONS, options),
    [options],
  );
  const mergedPlugins = useMemo(() => {
    const quadrantBackgroundPlugin =
      createQuadrantBackgroundPlugin(quadrantBackground);

    return [
      ...(quadrantBackgroundPlugin ? [quadrantBackgroundPlugin] : []),
      ...(showPointLabels ? [scatterPointLabelPlugin] : []),
      ...(plugins ?? []),
    ];
  }, [plugins, quadrantBackground, showPointLabels]);

  return (
    <>
      <Scatter
        ref={chartRef as RefObject<Chart<"scatter"> | null>}
        data={data}
        options={mergedOptions}
        plugins={mergedPlugins}
      />
      {children}
    </>
  );
}

export { ChartJsScatterChart };
export type { ChartJsScatterChartProps, ScatterQuadrantBackground };
